import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from "react-i18next";
import PhoneInput from 'react-phone-input-2';
import "react-phone-input-2/lib/style.css";
import { PHONE_INPUT_STYLE, PHONE_INPUT_BUTTON_STYLE, PHONE_INPUT_CONTAINER_STYLE } from './styles';
import { normalizePhoneNumber } from '../../../utils/phoneNumber';

import { ReactComponent as MySpinner } from '../../../animations/mySpinner.svg';
import NotificationDialog from '../Popup/NotificationDialog';
import { useCheckPhoneMutation } from '../../../features/details/detailsApiSlice';
import { useRegistrationMutation } from '../../../features/auth/authApiSlice';
import { setAuth } from '../../../features/auth/authSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const Registration = ({ onClose, initialPhone, onPhoneChange }) => {
    const { t } = useTranslation();
    const [regData, setRegData] = useState(null);
    const { register, handleSubmit } = useForm();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [loading, setLoading] = useState(false);
    const [phone, setPhone] = useState(initialPhone || '');
    const [checkPhone] = useCheckPhoneMutation();
    const [isClientChecked, setIsClientChecked] = useState(true);
    const [email, setEmail] = useState('');
    const [emailPending, setEmailPending] = useState(false);
    const [resendSeconds, setResendSeconds] = useState(60);
    const [registration] = useRegistrationMutation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isErrorOpen, setIsErrorOpen] = useState(false);
    const [errorText, setErrorText] = useState('');

    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [warningText, setWarningText] = useState('');

    const [isPassword, setIsPassword] = useState(true);
    const [isPasswordRepeat, setIsPasswordRepeat] = useState(true);
    const submitInFlightRef = useRef(false);

    useEffect(() => {
        if (initialPhone && !phone) setPhone(initialPhone);
    }, [initialPhone]);

    useEffect(() => {
        if (!emailPending || resendSeconds <= 0) return undefined;
        const timer = setInterval(() => setResendSeconds(value => Math.max(0, value - 1)), 1000);
        return () => clearInterval(timer);
    }, [emailPending, resendSeconds]);

    const onSubmit = async (data) => { 
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;
        try {

            setLoading(true);

            try {
                if (data.password !== data.passwordRepeat) {
                    throw new Error(t('InvalidTwicePasswordMessage'));
                }

                if (data.password.length < 8 || data.password.length > 20) {
                    throw new Error(t('InvalidPassword'));
                }
            } catch (error) {
                setWarningText(error.message);
                setIsWarningOpen(true);
                setLoading(false);
                console.error(error);
                return;
            }

            if (!firstName && firstName?.length == 0 && !lastName && lastName?.length == 0) {
                throw new Error(t('EmptyFieldsMessage'))
            }

            if (!phone) {
                throw new Error(t('EmptyPhoneNumberMessage'));
            }

            const formatPhone = normalizePhoneNumber(phone);
            setPhone(formatPhone);   
            onPhoneChange?.(formatPhone);

            const isPhoneExists = await checkPhone(formatPhone).unwrap();

            if (isPhoneExists) {
                throw new Error(t('PhoneExistsMessage'));
            }

            let newData = {
                ...data,
                phone: formatPhone,
                firstName,
                lastName,
                email: email.trim().toLowerCase(),
                method: 'email',
                language: window.localStorage.getItem('i18nextLng') || 'en'
            };
            delete newData.passwordRepeat;
            setRegData(newData);
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newData.email)) throw new Error(t('TheEmailIsNotValid'));
            const userData = await registration(newData).unwrap();
            if (!userData?.accessToken) throw new Error(t('RegistrationUnavailable'));
            dispatch(setAuth(userData));
            setEmailPending(true);
            setResendSeconds(60);
        } catch (error) {
            setErrorText(error.message);
            setIsErrorOpen(true);
            setLoading(false);
            console.error(error);
        } finally {
            submitInFlightRef.current = false;
        }
    };

    const isDigit = char => char >= '0' && char <= '9';

    const isSymbol = char => {
        const regex = /^[~:;.,!@#$%^&*()_+="><?\/|]+$/gm;
        return regex.test(char);
    }

    const isTwiceSymb = str => {
        const lastChar = str[str.length - 1];
        let counter1 = 0;
        let counter2 = 0;
        let counter3 = 0;
        
        if (lastChar === '-' || lastChar === `'` || lastChar === '`') {
            for (const char of str) {
                if (char === '-') counter1++;
                if (char === `'`) counter2++;
                if (char === '`') counter3++;
            }
        }

        return counter1 > 1 || counter2 > 1 || counter3 > 1;        
    };

    const onChange = val => {
        const lastChar = val[val.length - 1];
        
        return (isDigit(lastChar) || isSymbol(lastChar?.toLowerCase()) || isTwiceSymb(val))
            ? val.substr(0, val.length - 1)
            : val;
    }

    return <div className="popup-tab-content" id="register">
        {emailPending ? <>
            <div className="welcome-text">
                <h3>{t('PleaseCheckYourEmail')}</h3>
                <span>{t('ConfirmationLetterSentTo', { email })}</span>
                <p style={{ marginTop: '12px' }}>{t('CheckSpamFolder')}</p>
            </div>
            <button type="button" disabled={resendSeconds > 0} className="button full-width ripple-effect" onClick={async () => {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/registration/email/resend`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, language: window.localStorage.getItem('i18nextLng') || 'en' }) });
                setResendSeconds(60);
            }}>{resendSeconds > 0 ? `${t('ResendIn')} ${resendSeconds}s` : t('ClickToResend')}</button>
            <button type="button" style={{ marginTop: '10px', border: 0, background: 'transparent', color: '#2a41e8', cursor: 'pointer' }} onClick={() => { onClose(); navigate(regData?.accountType === 'master' ? '/master-office/settings' : '/client-office/settings'); }}>{t('ChangeEmail')}</button>
        </> : <>
            <form onSubmit={handleSubmit(onSubmit)} id="register-account-form">
                <div className="welcome-text">
                    <h3>{t('LetsCreateYourAccount')}</h3>
                </div>

                <div className="account-type">

                    <div>
                        <input checked={isClientChecked} onClick={() => setIsClientChecked(true)}
                            value="client" type="radio"
                            {...register('accountType', { required: true })}
                            name="accountType" id="client-radio" className="account-type-radio" />
                        <label htmlFor="client-radio" className="ripple-effect-dark"><i className="icon-material-outline-account-circle"></i> {t("Client")}</label>
                    </div>

                    <div>
                        <input checked={!isClientChecked} onClick={() => setIsClientChecked(false)}
                            value="master" type="radio" 
                            {...register('accountType', { required: true })}
                            name="accountType" id="master-radio" className="account-type-radio" />
                        <label htmlFor="master-radio" className="ripple-effect-dark"><i className="icon-material-outline-business-center"></i> {t('Master')}</label>
                    </div>

                </div>

                <div className="input-with-icon-left">
                    <i className="icon-feather-user"></i>
                    <input type="text" title='Only letters' autoComplete='off' placeholder={t("FirstName")} 
                    value={firstName} 
                    onChange={e => setFirstName(onChange(e.target.value))}
                    className="input-text with-border" name="firstName" id="firstName-register" />
                </div>

                <div className="input-with-icon-left">
                    <i className="icon-feather-user"></i>
                    <input type="text" title='Only letters' autoComplete='off' placeholder={t("LastName")} 
                    value={lastName}
                    onChange={e => setLastName(onChange(e.target.value))}
                    className="input-text with-border" name="lastName" id="lastName-register" />
                </div>

                <PhoneInput 
                disableDropdown={false}
                enableSearch={true}
                preferredCountries={['ua', 'cz', 'us']}
                inputProps={{
                    name: 'phone',
                    required: true,
                    autoFocus: true,                    
                }}
                inputStyle={PHONE_INPUT_STYLE}                
                containerStyle={PHONE_INPUT_CONTAINER_STYLE}
                buttonStyle={PHONE_INPUT_BUTTON_STYLE}
                country={"ua"} 
                value={phone} 
                onChange={value => {
                    setPhone(value);
                    onPhoneChange?.(value.startsWith('+') ? value : `+${value}`);
                }} />

                <div className="input-with-icon-left">
                    <i className="icon-material-outline-email"></i>
                    <input type="email" value={email} onChange={event => setEmail(event.target.value)} className="input-text with-border" name="email" placeholder="Email" required />
                </div>

                <div className='input-with-eye-icon'>
                    <div className="input-with-icon-left" title="Should be at least 8 characters long" data-tippy-placement="bottom">
                        <i className="icon-material-outline-lock"></i>
                        <input type={isPassword ? 'password' : 'text'} {...register('password', { required: true })} 
                        className="input-text with-border" name="password" id="password-register" placeholder={t("Password")} required />
                    </div>
                    <i className={`icon-feather-eye${isPassword ? '' : '-off'}`} onClick={() => setIsPassword(!isPassword)}></i>
                </div>

                <div className='input-with-eye-icon'>
                    <div className="input-with-icon-left" title="Should be at least 8 characters long">
                        <i className="icon-material-outline-lock"></i>
                        <input type={isPasswordRepeat ? 'password' : 'text'} {...register('passwordRepeat', { required: true })} 
                        className="input-text with-border" name="passwordRepeat" id="password-repeat-register" placeholder={t("RepeatPassword")} required />
                    </div>
                    <i className={`icon-feather-eye${isPasswordRepeat ? '' : '-off'}`} onClick={() => setIsPasswordRepeat(!isPasswordRepeat)}></i>
                </div>
            </form>

            {loading
            ? <div>
                <span className='button full-width button-sliding-icon ripple-effect'>
                    <MySpinner style={{
                        display: 'inline-block',
                        marginBottom: '-10px',
                    }} />
                    {t("Register")}
                </span>
            </div>
            :<button type="submit" form="register-account-form" disabled={loading} className="margin-top-10 button full-width button-sliding-icon ripple-effect">
                
                {t("Register")} 
                <i className="icon-material-outline-arrow-right-alt"></i>
            </button>
            }
        </>}

        <NotificationDialog open={isErrorOpen} onClose={() => setIsErrorOpen(false)}>
            {errorText}
        </NotificationDialog>

        <NotificationDialog type='warning' open={isWarningOpen} onClose={() => setIsWarningOpen(false)}>
            {warningText}
        </NotificationDialog>
    </div>;
};

export default Registration;
